/**
 * Typed in-app link destinations. The parse direction of the web-paths route
 * contract (the inverse of the `webPaths.*` builders). Shared across web and
 * Android via the golden fixture in `fixtures/link-fixtures.json` — Android keeps
 * its own Kotlin parser but is verified against the same cases.
 */
export type LinkDestination =
	| { kind: "myTickets" }
	| { kind: "property"; id: string }
	| { kind: "propertyProjects"; id: string }
	| { kind: "project"; propertyId: string; projectId: string }
	| { kind: "projectTasks"; propertyId: string; projectId: string }
	| { kind: "propertyJournal"; id: string }
	| { kind: "projectJournal"; propertyId: string; projectId: string }
	| { kind: "chat"; propertyId: string; chatId: string }
	| { kind: "ticket"; propertyId: string; ticketId: string }
	| { kind: "invitation"; token: string }
	| { kind: "external"; url: string }
	| { kind: "unknown"; path: string }

/**
 * Parse an in-app path (no scheme/host; query+hash already stripped by the
 * caller) into a typed destination. A trailing slash is tolerated. Anything that
 * does not match the route grammar is `unknown` (carrying the normalized path).
 */
export function parsePath(pathname: string): LinkDestination {
	const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname
	const seg = path.split("/").filter(Boolean) // ["properties","p1",...]

	if (seg.length === 1 && seg[0] === "tickets") return { kind: "myTickets" }
	if (seg[0] === "invitations" && seg.length === 2) {
		return { kind: "invitation", token: seg[1] }
	}
	if (seg[0] === "properties" && seg[1]) {
		const id = seg[1]
		if (seg.length === 2) return { kind: "property", id }
		if (seg.length === 3 && seg[2] === "projects") {
			return { kind: "propertyProjects", id }
		}
		if (seg.length === 3 && seg[2] === "journal") {
			return { kind: "propertyJournal", id }
		}
		if (seg.length === 4 && seg[2] === "chats") {
			return { kind: "chat", propertyId: id, chatId: seg[3] }
		}
		if (seg.length === 4 && seg[2] === "tickets") {
			return { kind: "ticket", propertyId: id, ticketId: seg[3] }
		}
		if (seg.length === 4 && seg[2] === "projects") {
			return { kind: "project", propertyId: id, projectId: seg[3] }
		}
		if (seg.length === 5 && seg[2] === "projects" && seg[4] === "tasks") {
			return { kind: "projectTasks", propertyId: id, projectId: seg[3] }
		}
		if (seg.length === 5 && seg[2] === "projects" && seg[4] === "journal") {
			return { kind: "projectJournal", propertyId: id, projectId: seg[3] }
		}
	}
	return { kind: "unknown", path }
}

/** Normalize a URL to bare `protocol//host[:port]` for set membership. */
function originOf(url: URL): string {
	return `${url.protocol}//${url.host}`
}

function stripQueryHash(path: string): string {
	const q = path.search(/[?#]/)
	return q === -1 ? path : path.slice(0, q)
}

/**
 * Classify a link href into a typed destination (AC11). "Ours" iff the path
 * matches the route grammar AND the host is in the runtime-configured
 * `ownedOrigins` allow-list (current + legacy + aliases). Strict equality to the
 * current origin is intentionally NOT used. Relative `/...` paths are own-origin;
 * scheme-relative `//host` is treated as external.
 */
export function classifyHref(
	href: string,
	ownedOrigins: readonly string[],
): LinkDestination {
	if (href.startsWith("/") && !href.startsWith("//")) {
		return parsePath(stripQueryHash(href))
	}
	let url: URL
	try {
		url = new URL(href)
	} catch {
		return { kind: "external", url: href }
	}
	const ownedHosts = new Set(
		ownedOrigins.map((o) => {
			try {
				return originOf(new URL(o))
			} catch {
				return o
			}
		}),
	)
	if (!ownedHosts.has(originOf(url))) {
		return { kind: "external", url: href }
	}
	return parsePath(url.pathname)
}
